import React, { useState, useRef, useEffect } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Brain, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "What should we promote this week?",
  "Which campaign is underperforming?",
  "Which influencer generated the highest revenue?",
  "Which ad should be paused?",
  "Compare Meta Ads and influencer marketing.",
];

function renderMarkdown(text) {
  // lightweight: bold + lists + line breaks
  const lines = text.split("\n");
  return lines.map((l, i) => {
    const bolded = l.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^\s*[-*]\s+/.test(l)) return <li key={i} className="ml-4 list-disc" dangerouslySetInnerHTML={{ __html: bolded.replace(/^\s*[-*]\s+/, "") }} />;
    if (/^#{1,3}\s/.test(l)) return <p key={i} className="mt-2 font-display font-bold" dangerouslySetInnerHTML={{ __html: bolded.replace(/^#{1,3}\s/, "") }} />;
    return <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: bolded }} />;
  });
}

export default function AIInsights() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session] = useState(`insights-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (q) => {
    const question = q || input;
    if (!question.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/insights", { question, session_id: session });
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't analyse that right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      <PageHeader title="AI Insights" subtitle="Ask anything about your marketing data" icon={Brain} />

      <div className="flex-1 overflow-y-auto rounded-2xl border border-border bg-surface-raised p-6">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-violet to-brand-pink">
              <Brain size={32} weight="fill" className="text-white" />
            </div>
            <p className="font-display text-xl font-bold">Your marketing analyst, on demand</p>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">I answer using your live campaign, ad and influencer data. Try one of these:</p>
            <div className="mt-5 flex max-w-lg flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="insight-suggestion" className="rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium transition-colors hover:border-primary hover:text-primary">{s}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-gradient-to-r from-brand-violet to-brand-pink text-white" : "border border-border bg-surface"}`}>
                  {m.role === "ai" ? <div className="leading-relaxed">{renderMarkdown(m.text)}</div> : m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Sparkle size={16} className="animate-pulse text-brand-violet" /> Analysing your data…</div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
        <Input data-testid="insight-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about campaigns, ROAS, influencers…" className="rounded-full" />
        <button type="submit" disabled={loading} data-testid="insight-send" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white disabled:opacity-50">
          <PaperPlaneRight size={18} weight="fill" />
        </button>
      </form>
    </div>
  );
}
